import ensureError, { formatErrorForNotification } from '@/common/utils/error';
import { notifyZapier } from '@/common/utils/zapier';
import { getStudent } from '@/game/GameComponents/PhaserGame/GameInstance';
import { getSceneManager } from '@/game/GameComponents/PhaserGame/Managers/SceneManager';

export class PhaserAudioManager {
  static currentMusic?: Phaser.Sound.HTML5AudioSound;
  static currentAmbience?: Phaser.Sound.HTML5AudioSound;

  static musicVolume: number = 0.1;
  static DEFAULT_MUSIC_VOLUME = 0.1;
  static musicEnabled: boolean = true;
  static audioTweens: Record<string, Phaser.Tweens.Tween | null> = {};

  static sfxVolume: number = 1;
  static DEFAULT_SFX_VOLUME = 1;
  static sfxEnabled: boolean = true;

  static activeSFX: SoundDict = {};

  static notifiedSFXKeys: string[] = [];

  static getExistingOrNewMusic(key: string): Phaser.Sound.HTML5AudioSound {
    const existingMusic = getSceneManager()?.getMainScene()?.sound.get(key);

    if (existingMusic) {
      return existingMusic as Phaser.Sound.HTML5AudioSound;
    }
    return getSceneManager()
      ?.getMainScene()
      ?.sound.add(key) as Phaser.Sound.HTML5AudioSound;
  }

  static loadAndStartMusic(key: string) {
    const scene = getSceneManager()?.getMainScene();
    if (!scene) return;

    if (!scene.sound.get(key)) {
      scene.load.audio(
        key,
        `game_assets/audio/phaser_game_audio/music/${key}.mp3`,
      );
      scene.load.start();
      scene.load.once('complete', () => {
        // If the music is already playing, resume it.
        if (key === PhaserAudioManager.currentMusic?.key) {
          PhaserAudioManager.setMusicPlaying(true);
        }
        PhaserAudioManager.setMusic(key);
      });
    } else {
      // If the music is already playing, resume it.
      if (key === PhaserAudioManager.currentMusic?.key) {
        PhaserAudioManager.setMusicPlaying(true);
      }
      PhaserAudioManager.setMusic(key);
    }
  }

  static setMusicPlaying(playing: boolean) {
    const scene = getSceneManager()?.getMainScene();
    if (!scene) return;

    const a = PhaserAudioManager;

    if (!a.currentMusic) return;
    if (playing) {
      if (!a.musicEnabled) return;

      if (a.currentMusic?.isPaused) {
        a.audioTweens[a.currentMusic.key] = a.fadeAudio(
          a.currentMusic,
          0,
          a.musicVolume,
          scene,
          1000,
        );
      } else {
        a.currentMusic?.play({ loop: true });
        a.audioTweens[a.currentMusic.key] = a.fadeAudio(
          a.currentMusic,
          0,
          a.musicVolume,
          scene,
          1000,
        );
      }
    } else {
      if (a.currentMusic.isPlaying) {
        // Fade out.
        a.audioTweens[a.currentMusic.key] = a.fadeAudio(
          a.currentMusic,
          a.musicVolume,
          0,
          scene,
          1000,
          () => {
            a.currentMusic?.pause();
          },
        );
      }
    }
  }

  static setMusic(key: string) {
    const scene = getSceneManager()?.getMainScene();
    if (!scene) return;

    const a = PhaserAudioManager;
    if (!a.musicEnabled) return;
    if (PhaserAudioManager.currentMusic?.key === key) return;

    let stoppedMusic = a.currentMusic;
    let newMusicStartDelay = 0;
    if (stoppedMusic) {
      newMusicStartDelay = 1000;
      a.audioTweens[stoppedMusic.key] = a.fadeAudio(
        stoppedMusic,
        a.musicVolume,
        0,
        scene,
        1000,
        () => {
          stoppedMusic?.stop();
        },
      );
    }
    // Wait for the old music to fade out before starting the new music.
    setTimeout(() => {
      a.currentMusic = a.getExistingOrNewMusic(key);

      if (!a.musicEnabled) {
        a.currentMusic.pause();
      } else {
        a.currentMusic.play({ loop: true });
        a.audioTweens[a.currentMusic.key] = a.fadeAudio(
          a.currentMusic,
          0,
          a.musicVolume,
          scene,
          1000,
        );
      }
    }, newMusicStartDelay);
  }

  static clearMusic(scene: Phaser.Scene) {
    const a = PhaserAudioManager;
    if (a.currentMusic) {
      a.audioTweens[a.currentMusic.key] = a.fadeAudio(
        a.currentMusic,
        a.currentMusic.volume,
        0,
        scene,
        1000,
        () => {
          a.currentMusic?.stop();
          a.currentMusic = undefined;
        },
      );
    }
  }

  static restartMusic() {
    if (!PhaserAudioManager.musicEnabled) return;
    const { currentMusic } = PhaserAudioManager;
    if (currentMusic && currentMusic?.manager !== null) {
      currentMusic.play({ loop: true });
    }
  }

  static setAmbience(key: string, scene: Phaser.Scene) {
    if (!PhaserAudioManager.sfxEnabled) return;
    if (
      PhaserAudioManager.currentAmbience &&
      PhaserAudioManager.currentAmbience?.manager !== null
    ) {
      this.fadeAudio(
        PhaserAudioManager.currentAmbience,
        0,
        PhaserAudioManager.currentAmbience.volume,
        scene,
        1000,
        () => {
          PhaserAudioManager.currentAmbience?.destroy();
          PhaserAudioManager.currentAmbience = scene.sound
            .add(key)
            .setVolume(0) as Phaser.Sound.HTML5AudioSound;
          PhaserAudioManager.currentAmbience.play({ loop: true });
          PhaserAudioManager.fadeAudio(
            PhaserAudioManager.currentAmbience,
            PhaserAudioManager.currentAmbience.volume,
            this.musicVolume,
            scene,
            1000,
          );
        },
      );
    } else {
      PhaserAudioManager.currentAmbience = scene.sound
        .add(key)
        .setVolume(0) as Phaser.Sound.HTML5AudioSound;
      PhaserAudioManager.currentAmbience.play();
      PhaserAudioManager.fadeAudio(
        PhaserAudioManager.currentAmbience,
        PhaserAudioManager.currentAmbience.volume,
        this.musicVolume,
        scene,
        0,
      );
    }
  }

  static setAmbiencePlaying(playing: boolean) {
    if (playing) {
      if (PhaserAudioManager.currentAmbience?.isPaused) {
        PhaserAudioManager.currentAmbience?.resume();
      } else {
        PhaserAudioManager.currentAmbience?.play();
      }
    } else {
      PhaserAudioManager.currentAmbience?.pause();
    }
  }

  static setMusicVolume(volume: number) {
    PhaserAudioManager.musicVolume = volume;
    PhaserAudioManager.currentMusic?.setVolume(volume);
  }

  static fadeAudio(
    audio: Phaser.Sound.HTML5AudioSound,
    from: number,
    target: number,
    scene: Phaser.Scene,
    duration: number,
    onComplete?: () => void,
  ): Phaser.Tweens.Tween {
    const existingTween = PhaserAudioManager.audioTweens[audio.key];
    if (existingTween) {
      existingTween.stop();
      PhaserAudioManager.audioTweens[audio.key] = null;
    }
    audio.setVolume(from);
    return scene.tweens
      .add({
        targets: {
          volume: from,
        },
        volume: target,
        duration,
        delay: 100,
        onStart: () => {
          if (audio.isPaused) {
            audio.resume();
            audio.setVolume(from);
          }
        },
        onComplete,
        onUpdate: (_tween, targets) => {
          audio.setVolume(targets.volume);
        },
      })
      .play();
  }

  static playSFX(
    scene: Phaser.Scene,
    sfx: string,
    options: SoundOptions = DEFAULT_SOUND_OPTIONS,
    onComplete?: () => void,
    isEssential: boolean = false, // Essential SFX include VO instructions.
  ) {
    if (!PhaserAudioManager.sfxEnabled && !isEssential) return;
    let existingSound = PhaserAudioManager.activeSFX[sfx];
    if (!existingSound) {
      try {
        existingSound = scene.sound.add(sfx, {
          volume: options.randomizeVolume
            ? Phaser.Math.FloatBetween(options.volumeMin!, options.volumeMax!)
            : options.volumeMin,
          rate: options.randomizePitch
            ? Phaser.Math.FloatBetween(options.pitchMin!, options.pitchMax!)
            : options.pitchMin,
        }) as Phaser.Sound.HTML5AudioSound;
      } catch (e) {
        const error = ensureError(e);
        console.error(error);
        if (!PhaserAudioManager.notifiedSFXKeys.includes(sfx)) {
          notifyZapier({
            hook: 'error',
            userId: getStudent().id,
            organisationId: getStudent().organisationId,
            notifier: scene.scene.key,
            fatal: false,
            error: formatErrorForNotification(error),
            context: `Missing SFX: ${sfx}`,
          });
          PhaserAudioManager.notifiedSFXKeys.push(sfx);
        }
        return;
      }
    }
    PhaserAudioManager.activeSFX[sfx] = existingSound;
    existingSound.play();
    if (onComplete) {
      existingSound.on('complete', onComplete);
    }
    return existingSound;
  }

  static stopSFX(scene: Phaser.Scene, sfx: string) {
    scene.sound.get(sfx)?.stop();
  }

  static destroyPhaserAudioManager() {
    PhaserAudioManager.currentMusic = undefined;
    PhaserAudioManager.currentAmbience = undefined;
    PhaserAudioManager.activeSFX = {};
    PhaserAudioManager.audioTweens = {};
  }
}

export const DEFAULT_SOUND_OPTIONS: SoundOptions = {
  randomizeVolume: false,
  volumeMin: 1,
  volumeMax: 1,
  randomizePitch: false,
  pitchMin: 1,
  pitchMax: 1,
};

export const RANDOM_SOUND_OPTIONS: SoundOptions = {
  randomizeVolume: true,
  volumeMin: 0.8,
  volumeMax: 1.15,
  randomizePitch: true,
  pitchMin: 0.9,
  pitchMax: 1.1,
};

export interface SoundOptions {
  randomizeVolume: boolean;
  volumeMin?: number;
  volumeMax?: number;
  randomizePitch: boolean;
  pitchMin?: number;
  pitchMax?: number;
}

interface SoundDict {
  [key: string]: Phaser.Sound.HTML5AudioSound;
}
